import moment from 'moment';
import { FormType } from '@fitogest/reactive-form-builder/interfaces';
import type { TratamientosControlModel } from 'src/app/models/tratamientos/tratamientos-control.model';
import { TratamientosControlRepeatableEditState } from './tratamientos-control-repeatable-edit-state';
import { TratamientosControlValueResolver } from './tratamientos-control-value-resolver';

type TratamientosControlEditControl = {
    readonly pristine: boolean;
    readonly value?: unknown;
    patchValue(value: unknown, options: { emitEvent: boolean }): void;
};

type TratamientosControlEditFormGroup = {
    get(fieldName: string): TratamientosControlEditControl | null;
};

export type TratamientosControlEditFormBuilderPort = {
    readonly model?: TratamientosControlEditFormGroup | null;
    updateForm(data: Record<string, unknown>, emit: boolean): void;
};

export type TratamientosControlEditStateHydratorContext = {
    readonly formType: FormType;
    readonly raw: Record<string, unknown>;
    readonly model: TratamientosControlModel;
    readonly formBuilder?: TratamientosControlEditFormBuilderPort | null;
};


/**
 * Hydrates the control form edition state from the legacy backend response.
 * The hydrator keeps all legacy fallbacks isolated while the form migrates to repeatable rows.
 */
export class TratamientosControlEditStateHydrator {
    public static apply(context: TratamientosControlEditStateHydratorContext): void {
        if (context.formType !== FormType.EDITION) {
            return;
        }

        this.patchFinca(context);
        this.patchParcelas(context);
        this.patchFecha(context);
        this.patchRepeatableRows(context);
    }

    private static patchFinca(context: TratamientosControlEditStateHydratorContext): void {
        const idFinca = context.raw['id_finca'];

        if (typeof idFinca !== 'string' && typeof idFinca !== 'number') {
            return;
        }

        context.model.id_finca = idFinca;
        context.formBuilder?.updateForm({ id_finca: idFinca }, true);
    }

    private static patchParcelas(context: TratamientosControlEditStateHydratorContext): void {
        const idsParcelasRaw = context.raw['ids_parcelas'];

        if (!idsParcelasRaw) {
            return;
        }

        const idsArray = TratamientosControlValueResolver.parseSerializedIds(idsParcelasRaw);

        context.formBuilder?.updateForm({ ids_parcelas: idsArray }, false);
        context.model.ids_parcelas = String(idsParcelasRaw);
    }

    private static patchFecha(context: TratamientosControlEditStateHydratorContext): void {
        const fechaRaw = context.raw['fecha'];

        if (!fechaRaw) {
            return;
        }

        const control = context.formBuilder?.model?.get('fecha');

        if (!control?.pristine) {
            return;
        }

        control.patchValue(this.normalizeFecha(fechaRaw), { emitEvent: false });
    }

    private static patchRepeatableRows(context: TratamientosControlEditStateHydratorContext): void {
        const repeatablePatch = TratamientosControlRepeatableEditState.buildPatch(context.raw);

        if (!TratamientosControlRepeatableEditState.hasActiveRows(repeatablePatch.lineas_materias)) {
            return;
        }

        context.formBuilder?.updateForm(repeatablePatch, true);
    }

    private static normalizeFecha(raw: unknown): string {
        const fecha = String(raw);

        return /^\d{4}-\d{2}-\d{2}$/.test(fecha)
            ? fecha
            : moment(fecha, 'DD/MM/YYYY', true).format('YYYY-MM-DD');
    }
}
